import { datadogLogs } from "@datadog/browser-logs";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useWaitForPrevious } from "@redotech/react-util/wait-for-previous";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { MerchantAppTopic } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server";
import { MerchantAppEventServerContext } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server-provider";
import { listen } from "@redotech/redo-merchant-app-common/events/utils";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { UserCacheContext } from "@redotech/redo-merchant-app-common/user-cache";
import {
  convertToMinimalConversation,
  ExpandedConversation,
  isExpandedConversation,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { TeamConversationActivity } from "@redotech/redo-model/conversation-activity";
import { RenderedTeam } from "@redotech/redo-model/team";
import { FrontendTeamNoticeEvent } from "@redotech/redo-model/team-notifications/team-notifications";
import { CardListRef } from "@redotech/redo-web/card-list";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { TableRef } from "@redotech/redo-web/table";
import { idEqual } from "@redotech/util/equal";
import { sinkPromise } from "@redotech/util/promise";
import clone from "lodash/clone";
import {
  Component,
  createContext,
  ErrorInfo,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { CachedMinimalConversationFetcher } from "./cached-minimal-conversation-fetcher";
import { getConversation } from "./client/conversations";
import { KnowledgeCaptureProvider } from "./context/knowledge-capture-context";
import { UpdateConversationStateContext } from "./context/update-conversations-context";
import { ActiveViewContextProvider } from "./conversations-table-filters/active-view-context";
import { ActiveViewConversationCountsProvider } from "./conversations-table-filters/conversation-counts-context";
import {
  FiltersProvider,
  FinalizedFiltersContext,
} from "./conversations-table-filters/filters-context";
import { ConversationsTableSelection } from "./conversations-table/conversations-table-selection";
import { FullConversationsTable } from "./conversations-table/full-conversations-table";
import { GenericConversationFetcherCache } from "./generic-cached-conversation-fetcher";
import { maybeRunConversationsRefresh } from "./handle-notice-event";
import { KnowledgeCaptureRenderer } from "./knowledge-capture/knowledge-capture-renderer";
import {
  ACTIVE_CONVERSATION_QUERY_PARAMETER,
  getQueryParameter,
} from "./query-parameters";
import { SingleConversationView } from "./single-conversation-view";
import { SupportBillingAcceptanceModal } from "./support-billing-acceptance-modal";
import { SupportBreadcrumbOverrider } from "./support-breadcrumb-overrider";
import {
  getConversationActivityStream as getTeamConversationActivityStream,
  sendConversationActivityUpdate,
} from "./utils";
import { ViewsContext } from "./views";

/** Error boundary that silently catches errors and renders nothing on failure */
class SilentErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    try {
      datadogLogs.logger.info("SilentErrorBoundary caught error", {
        error,
        errorInfo,
      });
    } catch {
      // Logging failed - stay silent
    }
  }

  override render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

export const Support = memo(function Support() {
  const team = useContext(TeamContext);
  const user = useContext(UserContext);
  const views = useContext(ViewsContext);
  if (team && user && views) {
    return (
      <ActiveViewContextProvider>
        <FiltersProvider>
          <SupportComponent />
        </FiltersProvider>
      </ActiveViewContextProvider>
    );
  } else {
    return null;
  }
});

export const TeamConversationActivityContext =
  createContext<TeamConversationActivity>({});

const SupportComponent = memo(function SupportComponent() {
  const navigate: NavigateFunction = useNavigate();
  const team = useRequiredContext(TeamContext) as RenderedTeam;
  const user = useRequiredContext(UserContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const userCache = useContext(UserCacheContext);
  const filters = useContext(FinalizedFiltersContext);

  // The conversation that is currently being viewed in the detail view
  const [activeConversation, setActiveConversationState] =
    useState<MinimalConversation>();
  const [prevConversation, setPrevConversation] =
    useState<MinimalConversation>();
  const activeConversationRef = useRef<MinimalConversation>();
  const [pageLoaded, setPageLoaded] = useState(false);

  const [blockRefresh, setBlockRefresh] = useState(false);

  const [teamConversationActivity, setTeamConversationActivity] =
    useState<TeamConversationActivity>({});
  const teamConversationActivityRef = useRef<TeamConversationActivity>({});

  // Needed to prevent us from rendering the table before we get the active conversation from the network request
  const activeConversationLoading = useRef(true);

  // We shouldn't load anything until we've checked the URL for a potential active conversation
  const doneCheckingUrlForConversation = useRef(false);

  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    teamConversationActivityRef.current = teamConversationActivity;
  }, [teamConversationActivity]);

  useEffect(() => {
    const abortController = new AbortController();
    let retryCount = 0;
    const maxRetries = 2;
    const retryDelay = 2000;

    const setupStream = async () => {
      try {
        for await (const data of listen({
          query: async () => {
            return await getTeamConversationActivityStream({
              authorization: client.authorization() as {
                Authorization: string;
              },
              signal: abortController.signal,
            });
          },
          loopCondition: true,
        })) {
          const newTeamConversationActivity: TeamConversationActivity =
            data as any;
          setTeamConversationActivity(newTeamConversationActivity);
        }
      } catch (e) {
        if (
          (e instanceof Error && e.name === "AbortError") ||
          abortController.signal.aborted
        ) {
          return;
        }

        if (retryCount < maxRetries) {
          retryCount += 1;
          setTimeout(() => {
            void setupStream();
          }, retryDelay);
        }

        throw e;
      }
    };

    void setupStream();

    return () => {
      if (team.users.map((user) => user.user._id).includes(user._id))
        void sendConversationActivityUpdate({
          authorization: client.authorization() as { Authorization: string },
          prevConversationId: activeConversationRef.current?._id,
        });
      abortController.abort();
    };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** see useImperativeHandle in table.tsx, which wires this up */
  const tableRef = useRef<TableRef<MinimalConversation>>({
    refresh: async () => {},
    items: [],
    removeItem: () => {},
    externalRefresh: async () => {},
    externalSetItems: () => {},
  });

  /** see useImperativeHandle in card-list.tsx, which wires this up */
  const cardListRef = useRef<CardListRef<MinimalConversation>>({
    refresh: async () => {},
    removeItem: () => {},
    getNext: () => undefined,
    getPrevious: () => undefined,
    externalRefresh: async () => {},
    externalSetItems: () => {},
  });

  const refreshConversations = async () => {
    lastRefreshStartedAt.current = new Date();
    fetcher.invalidateCache();
    await Promise.all([
      tableRef.current?.externalRefresh?.(setConversations),
      cardListRef.current?.externalRefresh?.(setConversations),
    ]);
  };

  const throttledRefresh = useWaitForPrevious(refreshConversations);

  const refreshIgnoreResult = () => {
    sinkPromise(throttledRefresh());
  };

  const waitForRefresh = async () => {
    await throttledRefresh();
  };

  // If our latest refresh was triggered after we updated a conversation, we can set the conversations
  // Otherwise we should ignore the refresh - it's got stale data
  const setConversations = (conversations: MinimalConversation[]) => {
    if (!lastConversationUpdatedAt.current) {
      tableRef.current?.externalSetItems?.(conversations);
      cardListRef.current?.externalSetItems?.(conversations);
    } else if (
      lastRefreshStartedAt.current &&
      lastConversationUpdatedAt.current < lastRefreshStartedAt.current
    ) {
      tableRef.current?.externalSetItems?.(conversations);
      cardListRef.current?.externalSetItems?.(conversations);
    }
  };

  const lastRefreshStartedAt = useRef<Date | null>(null);
  const lastConversationUpdatedAt = useRef<Date | null>(null);

  const removeConversation = (conversation: MinimalConversation) => {
    lastConversationUpdatedAt.current = new Date();
    cardListRef.current?.removeItem(conversation, idEqual);
    tableRef.current?.removeItem(conversation, idEqual);
    // In the future we probably don't want to invalidate the cache here. We should perform an optimistic update, and if it's in the view, we should update the cache, and the table/card list
    fetcher.invalidateCache();
  };

  const optimisticUpdate = (conversation: MinimalConversation) => {
    // TODO implement
    // Check if conversation matches the view/filters
    // If so, update the cache, and the table/card list
    // Make sure to not only add the conversation, but remove if it no longer matches th view, or just update the conversation in memory
    // Update all cached pages - we may need to invalidate cache that doesn't match existing filters
    // Also increment/decrement the counts
  };

  const eventServer = useRequiredContext(MerchantAppEventServerContext);

  // The main purpose of the cache is to avoid making the same request when we switch between cards and tables
  // To avoid handling the complexity of cache invalidation, we invalidate the cache anytime we change the filters
  useEffect(() => {
    fetcher.invalidateCache();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.sort?.direction,
    filters.sort?.key,
    filters.advancedFilters,
    filters.search,
    filters.status,
    filters.drafts,
    filters.customerEmail,
  ]);

  useEffect(() => {
    const unlistenCallback = eventServer.subscribe({
      topic: MerchantAppTopic.TEAM,
      callback: async (message: FrontendTeamNoticeEvent | null) => {
        if (blockRefresh) {
          return;
        }
        maybeRunConversationsRefresh(message, refreshIgnoreResult);
      },
    });
    return () => unlistenCallback();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockRefresh]);

  useEffect(() => {
    if (team.users.map((user) => user.user._id).includes(user._id)) {
      if (!activeConversation) return;
      const abortController = new AbortController();
      const sendUpdate = async ({
        prevConversationId,
      }: { prevConversationId?: string } = {}) => {
        await sendConversationActivityUpdate({
          authorization: client.authorization() as { Authorization: string },
          activeConversationId: activeConversation._id,
          prevConversationId,
          signal: abortController.signal,
        });
      };

      // Optimistically update team conversation activity
      const newTeamConversationActivity = clone(
        teamConversationActivityRef.current,
      );
      // Remove from old conversation
      if (
        prevConversation &&
        newTeamConversationActivity[prevConversation._id]
      ) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        newTeamConversationActivity[prevConversation._id].viewing =
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          newTeamConversationActivity[prevConversation._id].viewing.filter(
            (userId) => userId !== user._id,
          );
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        newTeamConversationActivity[prevConversation._id].typing =
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          newTeamConversationActivity[prevConversation._id].typing.filter(
            (userId) => userId !== user._id,
          );
        if (
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          newTeamConversationActivity[prevConversation._id].viewing.length ===
            0 &&
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          newTeamConversationActivity[prevConversation._id].typing.length === 0
        ) {
          delete newTeamConversationActivity[prevConversation._id];
        }
      }
      // Add to new conversation
      if (!newTeamConversationActivity[activeConversation._id]) {
        newTeamConversationActivity[activeConversation._id] = {
          viewing: [],
          typing: [],
        };
      }
      const newViewing = [
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        ...(newTeamConversationActivity[activeConversation._id].viewing || []),
        user._id,
      ];
      newTeamConversationActivity[activeConversation._id] = {
        viewing: [...newViewing],
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        typing: newTeamConversationActivity[activeConversation._id].typing,
      };
      setTeamConversationActivity(newTeamConversationActivity);

      setPrevConversation(activeConversation);
      void sendUpdate({ prevConversationId: prevConversation?._id });
      const interval = setInterval(sendUpdate, 5 * 1000);
      return () => {
        clearInterval(interval);
        abortController.abort("activeConversation changed");
      };
    }
    return;
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?._id]);

  useEffect(() => {
    // Handle the case where we went back to the view without clicking the back button
    // (i.e. by clicking the view in the navigation sidebar)
    if (!location.search && activeConversation) {
      void setActiveConversation(undefined);
      activeConversationLoading.current = false;
    }

    const activeConversationId = getQueryParameter(
      location,
      ACTIVE_CONVERSATION_QUERY_PARAMETER,
    );
    if (
      activeConversationId &&
      activeConversation?._id !== activeConversationId
    ) {
      const updateActiveConversation = async () => {
        try {
          const conversation = await getConversation(client, {
            conversationId: activeConversationId,
          });
          await setActiveConversation(conversation);
        } finally {
          activeConversationLoading.current = false;
        }
      };
      void updateActiveConversation();
    } else {
      activeConversationLoading.current = false;
    }

    doneCheckingUrlForConversation.current = true;
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const fetcher: CachedMinimalConversationFetcher = useMemo(() => {
    return new CachedMinimalConversationFetcher(
      new GenericConversationFetcherCache<MinimalConversation>(),
      client,
      userCache,
      () => setPageLoaded(true),
    );
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const [fullConversation, setFullConversation] = useState<
    ExpandedConversation | undefined
  >(undefined);

  const setActiveConversation = useHandler(
    async (
      incomingConversation:
        | ExpandedConversation
        | MinimalConversation
        | undefined,
    ) => {
      setActiveConversationState((prevActiveConversation) => {
        let conversationToUse: MinimalConversation | undefined;
        if (!incomingConversation || !prevActiveConversation) {
          conversationToUse = incomingConversation;
        } else if (incomingConversation?._id !== prevActiveConversation?._id) {
          conversationToUse = incomingConversation;
        } else if (
          new Date(incomingConversation.updatedAt) >=
          new Date(prevActiveConversation.updatedAt)
        ) {
          conversationToUse = incomingConversation;
        } else {
          conversationToUse = prevActiveConversation;
        }

        // If we ever try to update the active conversation with an expanded conversation (probably from an update)
        // We should update our full conversation state to match
        if (conversationToUse && isExpandedConversation(conversationToUse)) {
          setFullConversation(conversationToUse);
          conversationToUse = convertToMinimalConversation(conversationToUse);
        }

        // Validate conversation has valid customer data if customer object exists
        if (
          conversationToUse?.customer &&
          !conversationToUse.customer.customer
        ) {
          console.error(
            "Conversation has customer object but missing customer.customer",
            { conversationId: conversationToUse._id },
          );
        }

        navigateToConversationIfNeeded(
          prevActiveConversation,
          conversationToUse,
        );

        return conversationToUse;
      });
    },
  );

  const navigateToConversationIfNeeded = (
    activeConversation: MinimalConversation | undefined,
    newConversation: MinimalConversation | undefined,
  ): void => {
    if (activeConversation?._id === newConversation?._id) {
      return;
    }
    const locationActiveConversationId = getQueryParameter(
      location,
      ACTIVE_CONVERSATION_QUERY_PARAMETER,
    );
    if (newConversation?._id === locationActiveConversationId) {
      return;
    }
    const searchParams = new URLSearchParams(location.search);

    if (newConversation?._id) {
      searchParams.set(
        ACTIVE_CONVERSATION_QUERY_PARAMETER,
        newConversation._id,
      );
      navigate(`${location.pathname}?${searchParams}`);
    } else {
      searchParams.delete(ACTIVE_CONVERSATION_QUERY_PARAMETER);
      navigate(`${location.pathname}?${searchParams}`);
    }
  };

  if (!team.settings.support?.enabled) {
    navigate(`/stores/${team._id}/support/about`);
  }

  const updateConversationStateContext: UpdateConversationStateContext = {
    invalidateCache: () => fetcher.invalidateCache(),
    tableRef,
    cardListRef,
    waitForRefresh,
    refreshIgnoreResult,
    removeConversation,
    optimisticUpdate,
  };

  const showConversationsTable = useMemo(() => {
    return (
      doneCheckingUrlForConversation.current &&
      !activeConversation &&
      !activeConversationLoading.current
    );
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    doneCheckingUrlForConversation.current,
    activeConversation,
    activeConversationLoading.current,
  ]);

  const showLoadingAnimation = useMemo(() => {
    return (
      doneCheckingUrlForConversation.current &&
      activeConversationLoading.current
    );
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    doneCheckingUrlForConversation.current,
    activeConversationLoading.current,
  ]);

  // Whenever the active conversation changes, we should load the full conversation,
  // but we also want to be able to just directly update the full conversation after
  // performing a simple update. So we basically use this to trigger network requests
  // when the active conversation changes, and we use the fullConversationLoad to directly
  // update the full conversation after a simple update.
  const fullConversationLoad = useLoad<
    ExpandedConversation | undefined
  >(async () => {
    let conversation: ExpandedConversation | undefined;
    setFullConversation(undefined);
    if (activeConversation?._id) {
      conversation = await getConversation(client, {
        conversationId: activeConversation._id,
      });
    }
    setFullConversation(conversation);
    return conversation;
  }, [activeConversation?._id]);

  return (
    <KnowledgeCaptureProvider>
      <SilentErrorBoundary>
        <KnowledgeCaptureRenderer />
      </SilentErrorBoundary>
      <UpdateConversationStateContext.Provider
        value={updateConversationStateContext}
      >
        <ActiveViewConversationCountsProvider
          blockCountRefreshes={blockRefresh}
        >
          <TeamConversationActivityContext.Provider
            value={teamConversationActivity}
          >
            <SupportBreadcrumbOverrider />
            <SupportBillingAcceptanceModal>
              {activeConversation && (
                <SingleConversationView
                  activeConversation={activeConversation}
                  blockRefresh={blockRefresh}
                  fetcher={fetcher}
                  fullConversation={fullConversation}
                  fullConversationLoading={fullConversationLoad.pending}
                  setActiveConversation={setActiveConversation}
                  setFullConversation={setFullConversation}
                />
              )}
              {showConversationsTable && (
                <ConversationsTableSelection fetcher={fetcher}>
                  <FullConversationsTable
                    blockRefresh={blockRefresh}
                    fetcher={fetcher}
                    pageLoaded={pageLoaded}
                    setActiveConversation={setActiveConversation}
                    setBlockRefresh={setBlockRefresh}
                  />
                </ConversationsTableSelection>
              )}
              {showLoadingAnimation && (
                <Flex align="center" justify="center" w="full">
                  <LoadingRedoAnimation />
                </Flex>
              )}
            </SupportBillingAcceptanceModal>
          </TeamConversationActivityContext.Provider>
        </ActiveViewConversationCountsProvider>
      </UpdateConversationStateContext.Provider>
    </KnowledgeCaptureProvider>
  );
});
